import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bubble, Sender } from '@ant-design/x';
import { RobotOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, Empty, Segmented, Select, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import { cancelQuery, getChatHistory, sendQuery, retryQuery } from '../services/api';
import './Chat.css';
import ChatSources from './ChatSources';

const { Text } = Typography;
const METHOD_OPTIONS = [
  { label: 'Global', value: 'global' },
  { label: 'Local', value: 'local' },
  { label: 'Drift', value: 'drift', disabled: true },
];
const CHAT_MODEL_OPTIONS = [
  { label: 'GPT-6 Astra (OpenAI)', value: 'gpt-6-astra' },
  { label: 'GPT-6.1 Sol (OpenAI)', value: 'gpt-6.1-sol' },
  { label: 'Claude Opus 5.5', value: 'claude-opus-5-5' },
  { label: 'Grok 4.6', value: 'grok-4.6' },
  { label: 'Gemini 3.8 Flash', value: 'gemini-3.8-flash' },
  { label: 'Mistral Medium 3.5', value: 'mistral-medium-3-5' },
  { label: 'DeepSeek V4.1 Flash', value: 'DeepSeek-V4.1-Flash' },
  { label: 'Qwen 3.8 27B', value: 'qwen--qwen38-27b' },
  { label: 'Kimi K3', value: 'kimi-k3' },
];
const normalizeChatModel = (model) => (
  model === 'claude-fable-5' ? 'claude-opus-5-5' : model || 'gpt-6-astra'
);

function MarkdownBubbleContent(content) {
  return <ReactMarkdown>{String(content ?? '')}</ReactMarkdown>;
}

function parseResponse(instance) {
  if (!instance || typeof instance !== 'object') {
    console.error('Invalid chat history item.');
    return null
  }

  const query = instance.query;
  const fullResponse = instance.content;
  const qid = instance.id;

  if (!query || typeof query !== 'string') {
    console.error('Invalid chat history response payload.');
    return null
  }

  const userMessage = { sender: 'user', text: query, qid };

  if (!fullResponse || typeof fullResponse !== 'object') {
    return [userMessage, { sender: 'system', text: 'Query failed.', qid}];
  }

  const response = fullResponse.response;
  const context = fullResponse.context || null;
  const botMessage = response? {
    sender: 'bot',
    text: typeof response === 'string' ? response : JSON.stringify(response ?? ''),
    qid
  } : null;

  if (context && botMessage) {
    botMessage.context = normalizeContext(context);
    botMessage.sourceContext = context;
  }

  return [userMessage, botMessage || { sender: 'system', text: 'Query failed.', qid }];
}

function normalizeContext(context) {
  if (!context) {
    return [];
  }

  return context.reports
    ? context.reports.map((item) => ({
        id: item.id,
        weight: item['occurrence weight'] ? item['occurrence weight'] : 1,
      }))
    : [];
}

const ERROR_STRING = "I am sorry but I am unable to answer this question given the provided data";

function Chat({ graphId, initialChatModel, sendTriggerSignal, setContextCommunities }) {
  const [messages, setMessages] = useState([{ sender: 'bot', text: 'Hello! Ask about your graph once a job is loaded.' }]);
  const [method, setMethod] = useState('global');
  const [chatModel, setChatModel] = useState(normalizeChatModel(initialChatModel));
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [latestQID, setLatestQID] = useState(null);
  const [latestError, setLatestError] = useState(false);
  const bubbleListRef = useRef(null);

  useEffect(() => {

    if (bubbleListRef.current?.scrollBoxNativeElement) {
      bubbleListRef.current.scrollTo({ top: 'bottom', behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    async function loadHistory() {
      try {
        const data = await getChatHistory(graphId);
        const flattenedHistory = data.flatMap((instance) => {
          try {
            const parsed = parseResponse(instance);
            return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
          } catch (err) {
            console.error('Failed to parse chat history entry; rendering as error.', err, instance);
            return [
              { sender: 'user', text: instance?.query || '(missing query)', qid: instance?.id },
              { sender: 'system', text: 'Query failed.', qid: instance?.id },
            ];
          }
        });
        setMessages(
          flattenedHistory.length > 0
            ? flattenedHistory
            : [{ sender: 'bot', text: 'No conversation history yet. Start by asking a question about this graph.' }]
        );
        // set states w.r.t the last message
        const latestQuery = data[data.length - 1];
        if (!latestQuery) {
          return;
        }
        setLatestQID(latestQuery.id);
        const latestStatus = latestQuery.status;
        const latestResponse = latestQuery.content?.response;
        if (latestStatus === 'pending' || latestStatus === 'running') {
          setThinking(true);
        } else if (
          latestStatus === 'error' ||
          latestStatus === 'cancelled' ||
          (typeof latestResponse === 'string' && latestResponse.includes(ERROR_STRING))
        ) {
          setLatestError(true);

        }
      } catch (error) {
        setMessages([{ sender: 'system', text: error?.message || 'Failed to load chat history.' }]);
        setLatestError(true);
      }
    }

    if (graphId) {
      loadHistory();
    } else {
      setMessages([{ sender: 'bot', text: 'Hello! Ask about your graph once a job is loaded.' }]);
      setInput('');
      setThinking(false);
      setLatestQID(null);
    }
  }, [graphId]);

  useEffect(() => {
    setChatModel(normalizeChatModel(initialChatModel));
  }, [graphId, initialChatModel]);

  async function handleCancel() {

    if (!latestQID) {
      return;
    }

    try {
      await cancelQuery(latestQID);
      setMessages((msgs) => [...msgs, { sender: 'system', text: 'Cancelled.' }]);
      setLatestError(true);
    } catch (error) {
      setMessages((msgs) => [...msgs, { sender: 'system', text: error.message || 'An error occurred.' }]);
    } finally {
      setThinking(false);
    }
  }

  async function handleSend(message) {

    const question = String(message ?? input).trim();
    if (!question || !graphId || thinking) {
      return;
    }
    setMessages((msgs) => [...msgs, { sender: 'user', text: question }]);
    setInput('');
    setThinking(true);

    const qid = [null]
    const setQID = (id) => {
      qid[0] = id;
    }

    try {
      const response = await sendQuery(method, question, graphId, chatModel, setLatestQID, setQID);
      if (response === "cancelled.") {
        // handleCancel already pushed the 'Cancelled.' system message; nothing more to do.
        return;
      }
      // Patch the qid onto the last user message now that the backend assigned one.
      setMessages((msgs) => msgs.map((msg, i) =>
        i === msgs.length - 1 && msg.sender === 'user' ? { ...msg, qid: qid[0] } : msg
      ));

      const context = normalizeContext(response.context);
      const botMessage = response.response;
      if (botMessage.includes(ERROR_STRING)) {
        setMessages((msgs) => [
          ...msgs,
          { sender: 'system', text: 'An error occured internally when querying GraphRAG.', context, qid: qid[0] },
        ]);
        setLatestError(true);
      } else {
        setMessages((msgs) => [
          ...msgs,
          { sender: 'bot', text: response.response, context, sourceContext: response.context, qid: qid[0] },
        ]);
        setLatestError(false);
      }
    } catch (error) {
      if (qid[0] == null) {
        // POST failed before the backend created a query — roll back the optimistic
        // user bubble and restore the question text so the user can resend.
        // latestQID / latestError are intentionally untouched: the previous query
        // (if any) is still the only retryable one.
        setMessages((msgs) => {
          const last = msgs[msgs.length - 1];
          const trimmed = last && last.sender === 'user' && last.qid == null ? msgs.slice(0, -1) : msgs;
          return [...trimmed, { sender: 'system', text: error.message || 'An error occurred. Please try again.' }];
        });
        setInput(question);
      } else {
        // In-band error: backend query exists. Patch user bubble, append system error.
        setMessages((msgs) => msgs.map((msg, i) =>
          i === msgs.length - 1 && msg.sender === 'user' ? { ...msg, qid: qid[0] } : msg
        ));
        setMessages((msgs) => [...msgs, { sender: 'system', text: error.message || 'An error occurred. Please try again.', qid: qid[0] }]);
        setLatestError(true);
      }
    } finally {
      setThinking(false);
    }
  }

  const handleRetry = useCallback(async (qid) => {
    try {
      setThinking(true);
      const response = await retryQuery(method, qid, graphId, chatModel, setLatestQID)
      if (response === "cancelled.") {
        return;
      }
      const context = normalizeContext(response.context);
      const botMessage = response.response;
      if (botMessage.includes(ERROR_STRING)) {
        setMessages((msgs) => [
          ...msgs,
          { sender: 'system', text: 'An error occured internally when querying GraphRAG.', context, qid },
        ]);
        setLatestError(true);
      } else {
        setMessages((msgs) => [
          ...msgs,
          { sender: 'bot', text: response.response, context, sourceContext: response.context, qid },
        ]);
        setLatestError(false);
      }
    } catch (error) {
      setMessages((msgs) => [...msgs, { sender: 'system', text: error.message || 'An error occurred. Please try again.', qid }]);
      setLatestError(true);
    } finally {
      setThinking(false);
    }
  }, [method, graphId, chatModel]);

  const bubbleItems = useMemo(() => {

    const items = messages.map((message, index) => ({
      key: `${message.sender}-${index}`,
      qid: message.qid,
      role: message.sender === 'user' ? 'user' : message.sender === 'system' ? 'system' : 'ai',
      content: message.text,
      extraInfo: {
        qid: message.qid,
        message: message
      }
    }));

    if (thinking) {
      items.push({
        key: 'loading-bubble',
        role: 'ai',
        content: 'Thinking...',
        loading: true,
      });
    }

    return items;
  }, [messages, thinking]);

  const roles = useMemo(
    () => ({
      ai: {
        placement: 'start',
        variant: 'shadow',
        shape: 'corner',
        styles: {
          content: {
            background: '#ffffff',
            padding: '12px 16px 8px',
          },
        },
        avatar: <Avatar icon={<RobotOutlined />} className="chat-x-avatar chat-x-avatar--bot" />,
        contentRender: (content) => MarkdownBubbleContent(content),
        footer: (_, info) => {
          const message = info.extraInfo?.message;
          if (['global', 'fulltext'].includes(message?.sourceContext?.mode)) {
            return <ChatSources context={message.sourceContext} />;
          }
          if (message?.context && message.context.length > 0) {
            return (
              <Button
                size="small"
                type="default"
                onClick={() => {
                  sendTriggerSignal();
                  setContextCommunities(message.context);
                }}
              >
                Show context
              </Button>
            );
          }
          return null;
        },
        loadingRender: () => <Text>Thinking...</Text>,
      },
      user: {
        placement: 'end',
        variant: 'shadow',
        shape: 'corner',
        styles: {
          content: {
            background: '#ffffff',
            padding: '10px 16px 0px',
          },
        },
        avatar: <Avatar icon={<UserOutlined />} className="chat-x-avatar chat-x-avatar--user" />,
        contentRender: (content) => MarkdownBubbleContent(content),
        footer: (_, info) => {
          const qid = info.extraInfo?.qid;
          if (qid === latestQID && latestError) {
            return (
              <Button
                size="small"
                onClick={()=>handleRetry(qid)}
              >
                Retry
              </Button>
            );
          }
        }
      },
      system: {
        variant: 'borderless',
        shape: 'round',
        styles: {
          content: {
            padding: '6px 6px 0px',
          },
        },
        contentRender: (content) => MarkdownBubbleContent(content),
      },
    }),
    [latestQID, latestError, sendTriggerSignal, setContextCommunities, handleRetry]
  );

  return (
    <div className="chat-shell">
      <div className="chat-shell__header-top">
        <Segmented block options={METHOD_OPTIONS} value={method} onChange={setMethod} disabled={thinking} />
        <Select
          aria-label="Chat model"
          value={chatModel}
          options={CHAT_MODEL_OPTIONS}
          onChange={setChatModel}
          className="chat-model-select"
        />
      </div>

      {method === 'global' && (
        <Text type="secondary" className="chat-fulltext-hint">
          Searches graph summaries and original paper evidence across all sections, independently of graph view filters.
        </Text>
      )}
      <div className="chat-shell__messages">
        {bubbleItems.length === 0 && !thinking ? (
          <Empty description="No messages yet." />
        ) : (
          <Bubble.List
            ref={bubbleListRef}
            autoScroll
            items={bubbleItems}
            role={roles}
            className="chat-x-bubble-list"
          />
        )}
      </div>

      <div className="chat-shell__composer">
        <Sender
          value={input}
          onChange={(nextValue) => setInput(nextValue)}
          onSubmit={(nextValue) => handleSend(nextValue)}
          onCancel={handleCancel}
          loading={thinking}
          disabled={!graphId}
          placeholder={method === 'global' ? 'Ask about findings, methods, limitations or future research...' : 'Ask something about the selected graph...'}
          submitType="enter"
          autoSize={{ minRows: 1, maxRows: 5 }}
          className="chat-x-sender"
        />
      </div>
    </div>
  );
}

export default Chat;
